/**
 * Cores de badge dos apps web. Fonte única — antes existiam três cópias
 * (web-admin, web-partner, web-backoffice) que já tinham divergido em
 * quais cores oferecem e em como as escrevem.
 *
 * São classes Tailwind, não hex, porque badge precisa de par fundo+texto com
 * contraste garantido em cada tema, e a escala do Tailwind já entrega isso.
 *
 * Duas famílias ausentes de propósito:
 *   • `teal`  — é a cor de interação da marca. Badge turquesa vira "clicável".
 *   • `cyan`  — fica a 12° de matiz da turquesa; mesma confusão.
 * Ver docs/ref/mustauto_rebrand_plano.md §3.2.
 */

export type SoftColor =
  | 'slate'
  | 'gray'
  | 'red'
  | 'orange'
  | 'amber'
  | 'yellow'
  | 'green'
  | 'emerald'
  | 'blue'
  | 'indigo'
  | 'violet'
  | 'purple'
  | 'pink';

// ── Tema claro + escuro (web-admin, web-partner) ──────────────────────

export const SOFT_BADGE_CLASSES: Record<SoftColor, string> = {
  slate: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  gray: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  red: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300',
  orange: 'bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300',
  amber: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
  yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950/40 dark:text-yellow-300',
  green: 'bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-300',
  emerald: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
  blue: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
  indigo: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300',
  violet: 'bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300',
  purple: 'bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300',
  pink: 'bg-pink-100 text-pink-700 dark:bg-pink-950/40 dark:text-pink-300',
};

export const SOFT_TEXT_CLASSES: Record<SoftColor, string> = {
  slate: 'text-slate-700 dark:text-slate-300',
  gray: 'text-gray-700 dark:text-gray-300',
  red: 'text-red-700 dark:text-red-400',
  orange: 'text-orange-700 dark:text-orange-400',
  amber: 'text-amber-700 dark:text-amber-400',
  yellow: 'text-yellow-700 dark:text-yellow-400',
  green: 'text-green-700 dark:text-green-400',
  emerald: 'text-emerald-700 dark:text-emerald-400',
  blue: 'text-blue-700 dark:text-blue-400',
  indigo: 'text-indigo-700 dark:text-indigo-400',
  violet: 'text-violet-700 dark:text-violet-400',
  purple: 'text-purple-700 dark:text-purple-400',
  pink: 'text-pink-700 dark:text-pink-400',
};

export const SOFT_SURFACE_CLASSES: Record<SoftColor, string> = {
  slate: 'border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950/30',
  gray: 'border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-950/30',
  red: 'border-red-200 bg-red-50/60 dark:border-red-900/60 dark:bg-red-950/20',
  orange: 'border-orange-200 bg-orange-50/60 dark:border-orange-900/60 dark:bg-orange-950/20',
  amber: 'border-amber-200 bg-amber-50/60 dark:border-amber-900/60 dark:bg-amber-950/20',
  yellow: 'border-yellow-200 bg-yellow-50/60 dark:border-yellow-900/60 dark:bg-yellow-950/20',
  green: 'border-green-200 bg-green-50/60 dark:border-green-900/60 dark:bg-green-950/20',
  emerald: 'border-emerald-200 bg-emerald-50/60 dark:border-emerald-900/60 dark:bg-emerald-950/20',
  blue: 'border-blue-200 bg-blue-50/60 dark:border-blue-900/60 dark:bg-blue-950/20',
  indigo: 'border-indigo-200 bg-indigo-50/60 dark:border-indigo-900/60 dark:bg-indigo-950/20',
  violet: 'border-violet-200 bg-violet-50/60 dark:border-violet-900/60 dark:bg-violet-950/20',
  purple: 'border-purple-200 bg-purple-50/60 dark:border-purple-900/60 dark:bg-purple-950/20',
  pink: 'border-pink-200 bg-pink-50/60 dark:border-pink-900/60 dark:bg-pink-950/20',
};

/**
 * Variante sempre-escura, para o backoffice — que é escuro por natureza e não
 * põe a classe `.dark` no <html>, então as utilidades `dark:` acima não valem lá.
 */
export const SOFT_BADGE_CLASSES_DARK: Record<SoftColor, string> = {
  slate: 'bg-slate-500/15 text-slate-300',
  gray: 'bg-gray-500/15 text-gray-300',
  red: 'bg-red-500/15 text-red-300',
  orange: 'bg-orange-500/15 text-orange-300',
  amber: 'bg-amber-500/15 text-amber-300',
  yellow: 'bg-yellow-500/15 text-yellow-300',
  green: 'bg-green-500/15 text-green-300',
  emerald: 'bg-emerald-500/15 text-emerald-300',
  blue: 'bg-blue-500/15 text-blue-300',
  indigo: 'bg-indigo-500/15 text-indigo-300',
  violet: 'bg-violet-500/15 text-violet-300',
  purple: 'bg-purple-500/15 text-purple-300',
  pink: 'bg-pink-500/15 text-pink-300',
};

export const SOFT_TEXT_CLASSES_DARK: Record<SoftColor, string> = {
  slate: 'text-slate-300',
  gray: 'text-gray-300',
  red: 'text-red-300',
  orange: 'text-orange-300',
  amber: 'text-amber-300',
  yellow: 'text-yellow-300',
  green: 'text-green-300',
  emerald: 'text-emerald-300',
  blue: 'text-blue-300',
  indigo: 'text-indigo-300',
  violet: 'text-violet-300',
  purple: 'text-purple-300',
  pink: 'text-pink-300',
};

export const SOFT_SURFACE_CLASSES_DARK: Record<SoftColor, string> = {
  slate: 'border-slate-500/30 bg-slate-500/10 text-slate-200',
  gray: 'border-gray-500/30 bg-gray-500/10 text-gray-200',
  red: 'border-red-500/30 bg-red-500/10 text-red-200',
  orange: 'border-orange-500/30 bg-orange-500/10 text-orange-200',
  amber: 'border-amber-500/30 bg-amber-500/10 text-amber-200',
  yellow: 'border-yellow-500/30 bg-yellow-500/10 text-yellow-200',
  green: 'border-green-500/30 bg-green-500/10 text-green-200',
  emerald: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200',
  blue: 'border-blue-500/30 bg-blue-500/10 text-blue-200',
  indigo: 'border-indigo-500/30 bg-indigo-500/10 text-indigo-200',
  violet: 'border-violet-500/30 bg-violet-500/10 text-violet-200',
  purple: 'border-purple-500/30 bg-purple-500/10 text-purple-200',
  pink: 'border-pink-500/30 bg-pink-500/10 text-pink-200',
};

/**
 * Cor de badge por status de OS. É o mesmo mapa em todos os apps web: antes o
 * backoffice pintava "em execução" de ciano e "aguardando pagamento" de laranja,
 * enquanto o app do mecânico usava laranja e rosa — o mesmo status aparecia de
 * cores diferentes conforme quem estivesse olhando.
 *
 * Espelha as matizes de `OS_STATUS_COLORS` em `@mustauto/brand/status`.
 */
export const OS_STATUS_BADGE_COLOR: Record<string, SoftColor> = {
  in_analysis: 'blue',
  in_execution: 'orange',
  waiting_part: 'amber',
  outsourced: 'violet',
  completed: 'green',
  waiting_payment: 'pink',
  cancelled: 'gray',
  delivered: 'emerald',
};

export function softBadge(color: SoftColor): string {
  return SOFT_BADGE_CLASSES[color];
}
export function softText(color: SoftColor): string {
  return SOFT_TEXT_CLASSES[color];
}
export function softSurface(color: SoftColor): string {
  return SOFT_SURFACE_CLASSES[color];
}
export function softBadgeDark(color: SoftColor): string {
  return SOFT_BADGE_CLASSES_DARK[color];
}
export function softTextDark(color: SoftColor): string {
  return SOFT_TEXT_CLASSES_DARK[color];
}
export function softSurfaceDark(color: SoftColor): string {
  return SOFT_SURFACE_CLASSES_DARK[color];
}
